import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The five onboarding steps in display order. The label is what the
 * indicator renders above each circle; the index is one-based to match
 * the URL (`/app/onboarding/step/1` etc.) and the human-readable copy
 * ("Step 2 of 5"). Adding or removing a step here is the single edit
 * needed — the indicator widget renders whatever it's given.
 */
export const ONBOARDING_STEPS = [
  { id: 1, label: 'Company' },
  { id: 2, label: 'Address' },
  // "Store" rather than "BigCommerce" — BC is the only platform at
  // MVP, but the wizard step is platform-agnostic (Shopify /
  // WooCommerce / etc. land as additional cards inside the step).
  { id: 3, label: 'Store' },
  { id: 4, label: 'Carrier' },
  { id: 5, label: 'Billing' },
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]['id'];

interface StepIndicatorProps {
  readonly currentStep: OnboardingStepId;
}

/**
 * Horizontal progress indicator: five numbered circles, the current one
 * highlighted, completed ones marked with a check, future ones muted.
 * Lines connect adjacent circles and pick up the primary tint once both
 * neighbours are reached.
 *
 * `aria-current="step"` is set on the active marker so screen readers
 * announce which step the user is on. The whole indicator carries
 * `aria-label` describing position in the flow.
 */
export function StepIndicator({ currentStep }: StepIndicatorProps) {
  return (
    <nav
      aria-label={`Onboarding step ${String(currentStep)} of ${String(ONBOARDING_STEPS.length)}`}
      className="mb-8"
    >
      <ol className="flex items-start justify-between">
        {ONBOARDING_STEPS.map((step, index) => {
          const isComplete = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isLast = index === ONBOARDING_STEPS.length - 1;
          return (
            <li key={step.id} className="flex flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <div className="flex flex-1 justify-end">
                  {index > 0 ? (
                    <div
                      className={cn(
                        'h-px w-full',
                        isComplete || isCurrent ? 'bg-primary' : 'bg-border',
                      )}
                    />
                  ) : null}
                </div>
                <div
                  aria-current={isCurrent ? 'step' : undefined}
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                    isComplete && 'border-primary bg-primary text-primary-foreground',
                    isCurrent && 'border-primary text-primary bg-background',
                    !isComplete &&
                      !isCurrent &&
                      'border-border text-muted-foreground bg-background',
                  )}
                >
                  {isComplete ? <Check className="h-4 w-4" aria-hidden="true" /> : step.id}
                </div>
                <div className="flex flex-1 justify-start">
                  {!isLast ? (
                    <div className={cn('h-px w-full', isComplete ? 'bg-primary' : 'bg-border')} />
                  ) : null}
                </div>
              </div>
              <span
                className={cn(
                  'mt-2 text-xs',
                  isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
